/* eslint-disable no-undef */
/* eslint-disable no-inner-declarations */
import React, { useCallback, useEffect, useState, useReducer, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { __ } from '../../translations';
import { errorCodes, screenDetailTypes } from '../../config/constants';
import PasswordPopup from '../elements/PasswordPopup';
import LanguagePopup from '../elements/LanguagePopup';
import { maxLength, required, validAlphanumeric } from '../../helpers/validator';
import { EVENT } from '../../contexts/MixpanelConstants';
import { getMixpanelProfileProperties } from '../../contexts/MixpanelHelper';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import SelectProfileLoader from '../elements/SelectProfileLoader';
import h, { getNewContentRating, sanitizeText } from '../../helpers/common';
import url from '../../helpers/url';
import { NAVIGATION } from '../../config/constants';
import { addProfile, editProfileDone } from '../../store/reducers/profileSlice';
import user from '../../helpers/user';

import { Avatar, Box, FlexWrapper, InputField, RangeSlider, Switch, Text, Button, Breadcrumbs } from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import withPresetWidth from '@intigral/shared/lib/components/hocs/withPresetWidth/withPresetWidth';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { IStore } from '../../interfaces/StoreTypes';
import _ from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { useProfileList } from '../../utils/useUserProfile';
import { getUserInfo } from '../../utils/user';
import { HeaderWcp } from '@intigral/ui';
import { Link as componentLink } from 'react-router-dom';

const InputStyle = withResponsiveStyles(InputField);
const BoxWidth = withPresetWidth(Box);

export const AddProfile = (props) => {
    // Using React Query instead of Redux for configuration
    const { profile: profileConfig } = useConfigurationSections();
    const { defaultSettings, profileNameMaxLength, colors: profileColors } = (profileConfig as any) || {};
    const { isLoading, errCode, errMess, profile, isFormSubmitted } = useSelector((state: IStore) => state.profile);
    const { data: profileList } = useProfileList(getUserInfo());
    // profile data
    const [profileName, setProfileName] = useState('');
    const [passwordProtected, togglePasswordProtected] = useReducer(
        (checked) => !checked,
        (defaultSettings as any)?.passwordProtected
    );
    const [defaultAudioLang, setDefaultAudioLang] = useState(defaultSettings?.defaultAudioLang);
    const [defaultSubTitleLang, setDefaultSubTitleLang] = useState(defaultSettings?.defaultSubTitleLang);
    const [autoPlayNextEpisode, toggleAutoPlayNextEpisode] = useReducer(
        (checked) => !checked,
        (defaultSettings as any)?.autoPlayNextEpisode
    );
    const [kidModeEnable, toggleKidsMode] = useReducer((checked) => !checked, defaultSettings?.kidsMode);
    const [ratingValue, setRatingValue] = useState('R18');
    // Lock profile with PIN
    const [parentalLockOn, toggleParentalLock] = useState(true);
    // popups state
    const [showAudioChoosePopup, setShowAudioChoosePopup] = useState(false);
    const [showLanguageChoosePopup, setShowLanguageChoosePopup] = useState(false);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    // actions results
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [failedAdd, setFailedAdd] = useState(false);
    // back & redirect route
    const navigate = useNavigate();
    const location = useLocation();
    const [backPageRoute, setBackPageRoute] = useState(url.getDetailsPage(screenDetailTypes.PROFILE_SETTINGS));

    const [lockFormActions, setLockFormActions] = useState(false);

    const { track }: { track: any } = useContext(MixpanelContext);
    // profile color
    const profileColorIndex = useSelector((state: IStore) => {
        const profilesCount = (profileConfig as any)?.colors?.length;
        return _.difference(
            Array.from(Array(profilesCount).keys()),
            _.map(profileList.profiles, (o, i) => Number(o.avatar) || i)
        )[0];
    });

    // profile actions
    const dispatch = useDispatch();
    const addUserProfile = useCallback((profileData) => dispatch(addProfile(profileData)), [dispatch]);

    // loadingProfile popup states
    const [loadingProfile, setLoadingProfile] = useState<any>(null);

    // errors states
    const [profileNameError, setProfileNameError] = useState('');

    //
    const profileNameRef = useRef<HTMLDivElement | null>(null);
    const scrollToRef = (ref: { current: HTMLElement | null }) => {
        if (!ref?.current) return;
        window.scroll({ top: ref.current.offsetTop, behavior: 'smooth' });
    };
    const queryClient = useQueryClient();

    const validateForm = () => {
        if (!required(profileName)) {
            setProfileNameError(__('err_msg_empty_profile_name'));
            return false;
        }
        if (!maxLength(profileNameMaxLength)(profileName)) {
            setProfileNameError(__('err_msg_max_profile_name'));
            return false;
        }

        if (!validAlphanumeric(profileName)) {
            setProfileNameError(__(`err_msg_invalid_profile_name`));
            return false;
        }
        // profile name already used
        if (
            _.includes(
                _.map(profileList.profiles, (i) => (i['profileName'] || '').toLowerCase()),
                profileName.toLowerCase()
            )
        ) {
            setProfileNameError(__(`err_msg_${errorCodes.PROFILE_NAME_EXISTS}`));
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (failedAdd && errCode) {
            track(EVENT.PROFILE.PROFILES_ADD_FAILED, { errCode: errCode, errMess: errMess });
        }
    }, [failedAdd]);

    // change rating based on mode
    useEffect(() => {
        setRatingValue(kidModeEnable ? 'PG' : 'R18');

        if (passwordProtected) {
            togglePasswordProtected();
        }
    }, [kidModeEnable]);

    const addProfileData = () => {
        if (validateForm()) {
            if ((!passwordProtected && ratingValue === 'R18') || passwordValidated) {
                const profileData = {
                    profileName: sanitizeText(profileName.trim()),
                    profileSettings: {
                        avatar: profileColorIndex ? profileColorIndex.toString() : '',
                        newParentalControl: ratingValue,
                        defaultAudioLang: defaultAudioLang,
                        passwordProtected: passwordProtected,
                        defaultSubTitleLang: defaultSubTitleLang,
                        autoPlayNextEpisode: autoPlayNextEpisode,
                        parentalLockOn:
                            kidModeEnable && (h.isAccountRetrievalFeatureEnabled() ? parentalLockOn : false),
                    },
                    kidsMode: kidModeEnable,
                };
                addUserProfile(profileData);
                setFormSubmitted(true);
            } else {
                setShowPasswordPopup(true);
            }
        } else {
            scrollToRef(profileNameRef);
        }
    };

    useEffect(() => {
        if (passwordValidated) {
            setShowPasswordPopup(false);
            addProfileData();
        }
    }, [passwordValidated]);
    useEffect(() => {
        if (isFormSubmitted && !isLoading) {
            if (errCode) {
                setFailedAdd(true);
                setLoadingProfile(false);
                setLockFormActions(false);
            } else {
                setLockFormActions(true);
                setFailedAdd(false);
                queryClient.invalidateQueries({ queryKey: ['profiles'] });
                // To prevent any redirection if the profile was not created yet
                // Fix mixpanel undefined issue
                if (typeof profile !== 'undefined' && profile !== null) {
                    // Mixpanel event
                    track(
                        EVENT.PROFILE.PROFILES_ADD_SUCCESS,
                        getMixpanelProfileProperties({ ...profile, kidsMode: kidModeEnable ? true : false })
                    );

                    // For kids profiles, set showWelcomeScreen flag to trigger gamification modal
                    if (kidModeEnable && profile.profileGuid) {
                        const updatedProfile = {
                            ...profile,
                            showWelcomeScreen: true,
                            kidsMode: kidModeEnable,
                            date: Date.now(),
                        };
                        // Store in localStorage to be picked up by GamificationWelcomeModal
                        h.setStore('selectedProfile', updatedProfile);
                    }

                    // redirect to profile setting page
                    // local states value not updating due to slice, as this logic might change as part of slice migration.
                    navigate(url.route(location.state.fromRoute ?? backPageRoute));
                    setLoadingProfile(false);
                    dispatch(editProfileDone());
                }
            }
        } else if (isLoading) {
            setLoadingProfile({ profileName, avatar: profileColorIndex });
        }
    }, [errCode, isLoading, isFormSubmitted, profile]);

    useEffect(() => {
        // set back & redirect route from history
        if (location.state?.fromRoute) {
            setBackPageRoute(location.state.fromRoute);
        }
    }, [location.pathname, location.search]);

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';

    const newContentRating = getNewContentRating();
    return (
        <>
            <HeaderWcp
                logoProps={{
                    homeUrl: '/',
                    title: 'STC logo',
                    logoSrc: 'images/big-logo.svg',
                    LinkComponent: componentLink,
                }}
                menuProps={
                    <Breadcrumbs
                        items={[
                            {
                                linkComponent: componentLink,
                                transformLinkProps: ({ href }) => ({ to: href }),
                                href: `${h.getLang() === 'en' ? '/en/' : '/'}`,
                                text: __('profile_menu_home'),
                            },
                            {
                                linkComponent: 'a',
                                href: user.getMyAccountUrl(),
                                text: __('profile_menu_account'),
                            },
                            {
                                text: profileName,
                            },
                        ]}
                    />
                }
            />
            {/* already showing toast message no need to show this, because it persists in the state and always shows previous error */}
            {/* {failedAdd && errCode && (
                <div className="profile-form_notification error-notification">
                    <span className="profile-warning-icon">
                        <i className="icon-Warning" />
                    </span>
                    <p className="profile-warning-desc">
                        <span>{__(`err_msg_${errCode}`)}</span>
                    </p>
                </div>
            )} */}
            <div className="container" style={{ paddingBottom: 'calc(var(--step) * 10)' }}>
                <BoxWidth
                    theme={{ default: 'none', md: 'dark' }}
                    padding={{ default: '0', md: '6x' }}
                    presetWidth="medium"
                >
                    <FlexWrapper flexDirection="column" gap="2x">
                        <Text as="h4" variant="h4" margin="0" size={{ default: '24' }}>
                            {__('manage_profile_add_new_profile')}
                        </Text>
                        <Avatar
                            color="rgb(165, 78, 225)"
                            image="https://cdn.jawwy.tv/9/avatar-smile.svg"
                            dimension="medium"
                            currentRestriction={ratingValue}
                        />
                        <div ref={profileNameRef}>
                            <InputField
                                label={__('manage_profile_profile_name')}
                                type="text"
                                name="profile-display-name"
                                autoComplete="off"
                                preIcon="kids"
                                preIconSize="smaller"
                                preIconPCSize="smaller"
                                errorMessage={profileNameError}
                                value={profileName}
                                invalid={!!profileNameError}
                                onChange={(e) => {
                                    setProfileName(e.target.value);
                                    setProfileNameError('');
                                }}
                                data-test-id="profile-name"
                            />
                        </div>
                        <FlexWrapper justifyContent="between" paddingBottom="2x">
                            <Text as="h4" variant="h4" margin="0" alignment="start" size={{ default: '18' }}>
                                {__('manage_kid_mode_enable')}
                            </Text>
                            <Switch
                                data-test-id="kidsmode-toogle-btn"
                                onChange={toggleKidsMode}
                                checked={kidModeEnable}
                            />
                        </FlexWrapper>
                        {!kidModeEnable && (
                            <RangeSlider
                                data-test-id="parental-rating-field"
                                labels={newContentRating}
                                text={newContentRating?.map((level) => __(`parental_control_new_level_${level}_desc`))}
                                checked={newContentRating?.indexOf(ratingValue || 'R18')}
                                onChangeHandler={(value) => setRatingValue(newContentRating[value])}
                            />
                        )}
                        {kidModeEnable && h.isAccountRetrievalFeatureEnabled() && (
                            <InputStyle
                                margin="0"
                                placeholder={__('profile_enable_parental_lock')}
                                type="text"
                                preIcon="lock"
                                preIconSize="smaller"
                                preIconPCSize="smaller"
                                disabled
                                cta={
                                    <Switch
                                        data-test-id="parental-pin-togglebtn"
                                        onChange={() => toggleParentalLock(!parentalLockOn)}
                                        checked={parentalLockOn}
                                    />
                                }
                                value={__(parentalLockOn ? 'manage_profile_on' : 'manage_profile_off')}
                                data-test-id="parental-lock"
                            />
                        )}
                        {!kidModeEnable && (
                            <InputStyle
                                margin="0"
                                placeholder={__('manage_profile_password_protected')}
                                type="text"
                                preIcon="lock"
                                preIconSize="smaller"
                                preIconPCSize="smaller"
                                disabled
                                cta={
                                    <Switch
                                        data-test-id="parental-pin-togglebtn"
                                        onChange={togglePasswordProtected}
                                        checked={passwordProtected}
                                    />
                                }
                                value={__(passwordProtected ? 'manage_profile_on' : 'manage_profile_off')}
                                data-test-id="profile-lock"
                            />
                        )}
                        <InputStyle
                            margin="0"
                            label={__('manage_profile_default_audio_language')}
                            type="text"
                            preIcon="volume-low"
                            preIconSize="smaller"
                            preIconPCSize="smaller"
                            cta={__('manage_profile_change')}
                            ctaLinkOnClick={(e) => {
                                e.preventDefault();
                                setShowAudioChoosePopup(true);
                            }}
                            disabled
                            value={__(`manage_profile_${defaultAudioLang}`)}
                            data-test-id="audio-field"
                        />
                        <InputStyle
                            margin="0"
                            label={__('manage_profile_default_subtitles_language')}
                            type="text"
                            preIcon="globe"
                            preIconSize="smaller"
                            preIconPCSize="smaller"
                            cta={__('manage_profile_change')}
                            ctaLinkOnClick={(e) => {
                                e.preventDefault();
                                setShowLanguageChoosePopup(true);
                            }}
                            disabled
                            value={__(`manage_profile_${defaultSubTitleLang}`)}
                            data-test-id="subtitle-field"
                        />
                        <InputStyle
                            margin="0"
                            placeholder={__('manage_profile_autoplay_next_episode')}
                            type="text"
                            preIcon="play"
                            preIconSize="smaller"
                            preIconPCSize="smaller"
                            disabled
                            cta={
                                <Switch
                                    data-test-id="autoplay-togglebtn"
                                    onChange={toggleAutoPlayNextEpisode}
                                    checked={autoPlayNextEpisode}
                                />
                            }
                            value={__(autoPlayNextEpisode ? 'manage_profile_on' : 'manage_profile_off')}
                            data-test-id="autoplay-field"
                        />
                        <FlexWrapper
                            direction={{ default: 'column', md: 'row' }}
                            gap="3x"
                            itemCount={2}
                            paddingTop="3x"
                            className="profile-save"
                        >
                            <Button
                                onClick={() => {
                                    navigate(NAVIGATION.GOBACKTO_PREVIOUS_SCREEN);
                                }}
                                data-test-id="cancel-btn"
                            >
                                {__('manage_profile_cancel')}
                            </Button>
                            <Button primary disabled={lockFormActions} onClick={addProfileData} data-test-id="save-btn">
                                {__('manage_profile_save')}
                            </Button>
                        </FlexWrapper>
                    </FlexWrapper>
                </BoxWidth>
            </div>

            <LanguagePopup
                selectedLanguage={defaultAudioLang}
                showPopup={showAudioChoosePopup}
                onSelect={(lang) => setDefaultAudioLang(lang)}
                onClose={() => setShowAudioChoosePopup(false)}
            />
            <LanguagePopup
                selectedLanguage={defaultSubTitleLang}
                showPopup={showLanguageChoosePopup}
                onSelect={(lang) => setDefaultSubTitleLang(lang)}
                onClose={() => setShowLanguageChoosePopup(false)}
            />

            {showPasswordPopup && (
                <PasswordPopup
                    showPopup={showPasswordPopup}
                    description={__(B2B_localization + 'password_popup_description_addProfile')}
                    onSuccess={() => setPasswordValidated(true)}
                    onClose={() => setShowPasswordPopup(false)}
                />
            )}
            <SelectProfileLoader loadingProfile={loadingProfile} autoCloseLoading={false} />
        </>
    );
};
